import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { blogAPI } from '../services/api';
import { Calendar, Clock, ArrowLeft, Loader2, Tag } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

const BlogDetailPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPost = useCallback(async () => {
    try {
      setLoading(true);
      const data = await blogAPI.getPostBySlug(slug);
      setPost(data);
      setError(null);
    } catch {
      setError('Failed to load blog post. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchPost();
  }, [fetchPost]);

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <div className="pt-32 pb-20 px-4">
        <div className="max-w-4xl mx-auto">
          <Link to="/blog">
            <Button variant="ghost" className="mb-8 text-teal-700 hover:text-teal-800">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Button>
          </Link>

          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="w-8 h-8 text-teal-700 animate-spin" />
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="text-center py-20">
              <p className="text-red-600 text-lg mb-4">{error}</p>
              <Link to="/blog">
                <Button className="bg-teal-700 hover:bg-teal-800">
                  Go to Blog
                </Button>
              </Link>
            </div>
          )}

          {/* Blog Post Content */}
          {!loading && !error && post && (
            <article>
              {/* Header */}
              <header className="mb-8">
                <Badge className="bg-teal-100 text-teal-700 mb-4">
                  {post.category}
                </Badge>
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                  {post.title}
                </h1>
                <div className="flex flex-wrap items-center gap-6 text-gray-600">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    <span>{new Date(post.publishedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    <span>{post.readTime}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">By {post.author}</span>
                  </div>
                </div>
              </header>

              {/* Excerpt */}
              <div className="bg-teal-50 border-l-4 border-teal-700 p-6 mb-8">
                <p className="text-lg text-gray-700 italic">{post.excerpt}</p>
              </div>

              {/* Content */}
              <div className="prose prose-lg max-w-none mb-8">
                <div className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                  {post.content}
                </div>
              </div>

              {/* Tags */}
              {post.tags && post.tags.length > 0 && (
                <div className="border-t pt-8 mt-8">
                  <div className="flex items-center gap-3 flex-wrap">
                    <Tag className="w-5 h-5 text-gray-600" />
                    {post.tags.map((tag) => (
                      <Badge key={tag} variant="outline" className="border-teal-700 text-teal-700">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Author Info */}
              <div className="border-t border-b py-8 my-8">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl font-bold text-teal-700">Dr</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{post.author}</h3>
                    <p className="text-gray-600">
                      Medical Oncologist, Director & CEO at Shankara Cancer and Research Institute. 
                      EC Member of ISMPO and Executive Secretary of NESMO.
                    </p>
                  </div>
                </div>
              </div>

              {/* Call to Action */}
              <div className="bg-gradient-to-r from-teal-700 to-teal-600 rounded-2xl p-8 text-white text-center">
                <h3 className="text-2xl font-bold mb-4">Have Questions?</h3>
                <p className="text-lg mb-6 opacity-90">
                  Schedule a consultation to discuss your cancer care needs
                </p>
                <Link to="/">
                  <Button variant="secondary" size="lg" className="bg-white text-teal-700 hover:bg-gray-100">
                    Contact Dr. Koyyala
                  </Button>
                </Link>
              </div>
            </article>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default BlogDetailPage;